import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getCommunityLessonDetail,
  getTeacherLessonDetail,
  getTeacherLessonTests,
  postUseCommunityLesson,
  type LessonPresentationFields,
  type LessonWorkspaceMode,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { buildLoginPath } from "../auth/post-login-redirect";
import type { DemoTourStep } from "../student-ide/demoTourSteps";
import { StudentIdeBody, type LessonRuntimeMarkDoneSpec } from "../student-ide/StudentIdeBody";
import { StudentLessonAdvancedBody } from "../student-ide/StudentLessonAdvancedBody";
import { starterFilesToProjectFiles } from "../teacher/course-template-helpers";
import { teacherPreviewBackLink, type TeacherPreviewContext } from "../teacher/teacherPreviewLessonUrl";
import { buildLessonImmersiveTheme } from "../teacher/lessonImmersiveTheme";
import { ImmersiveSplash } from "../transition/ImmersiveSplash";
import { LAUNCH_STATE_FLAG } from "../transition/LessonLaunchProvider";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function TeacherLessonPreviewPage() {
  const [params] = useSearchParams();
  const courseId = params.get("courseId")?.trim() ?? "";
  const cohortId = params.get("cohortId")?.trim() ?? "";
  const lessonId = params.get("lessonId")?.trim() ?? "";
  const titleHint = params.get("title")?.trim() ?? "";
  const returnTo = params.get("returnTo")?.trim() ?? "";
  const communityMode = params.get("community") === "1";
  const iconName = params.get("iconName")?.trim() || undefined;
  const iconBgColor = params.get("iconBgColor")?.trim() || undefined;
  const iconFgColor = params.get("iconFgColor")?.trim() || undefined;
  const wsModeParam = params.get("workspaceMode")?.trim() || undefined;
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const location = useLocation();
  const navigate = useNavigate();
  const introMode = (location.state as Record<string, unknown> | null)?.[LAUNCH_STATE_FLAG] ? "arrive" : "wash";

  const [loadedAppearance, setLoadedAppearance] = useState<LessonPresentationFields | null>(null);

  const resolvedAppearance = useMemo(
    () => ({
      iconName: iconName ?? loadedAppearance?.iconName,
      iconBgColor: iconBgColor ?? loadedAppearance?.iconBgColor,
      iconFgColor: iconFgColor ?? loadedAppearance?.iconFgColor,
      workspaceMode: (wsModeParam ?? loadedAppearance?.workspaceMode) as LessonWorkspaceMode | undefined,
    }),
    [iconName, iconBgColor, iconFgColor, wsModeParam, loadedAppearance]
  );

  const immersiveTheme = useMemo(
    () =>
      lessonId
        ? buildLessonImmersiveTheme({
            ...resolvedAppearance,
            seed: lessonId,
          })
        : undefined,
    [lessonId, resolvedAppearance]
  );

  const previewCtx: TeacherPreviewContext = useMemo(
    () => ({
      lessonId,
      ...(courseId ? { courseId } : {}),
      ...(cohortId ? { cohortId } : {}),
      ...(titleHint ? { lessonTitle: titleHint } : {}),
    }),
    [lessonId, courseId, cohortId, titleHint]
  );
  const back = useMemo(() => {
    if (returnTo.startsWith("/")) {
      return { to: returnTo, label: "Back" };
    }
    return teacherPreviewBackLink(previewCtx);
  }, [previewCtx, returnTo]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [lessonTitle, setLessonTitle] = useState(titleHint || "main.py");
  const [initialMainPy, setInitialMainPy] = useState("");
  const [tourSteps, setTourSteps] = useState<readonly DemoTourStep[] | undefined>(undefined);
  const [previewLessonTests, setPreviewLessonTests] = useState<readonly LessonRuntimeMarkDoneSpec[]>([]);
  const [workspaceMode, setWorkspaceMode] = useState<"simple" | "advanced">("simple");
  const [starterFiles, setStarterFiles] = useState<{ path: string; content: string }[]>([]);

  const storageKey = useMemo(() => {
    if (communityMode) return `ccs-teacher-preview-community-${lessonId}`;
    if (cohortId && lessonId) return `ccs-teacher-preview-cohort-${cohortId}-${lessonId}`;
    if (courseId && lessonId) return `ccs-teacher-preview-${courseId}-${lessonId}`;
    if (lessonId) return `ccs-teacher-preview-lesson-${lessonId}`;
    return "ccs-teacher-preview";
  }, [communityMode, courseId, cohortId, lessonId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!bearer || !lessonId || !UUID_RE.test(lessonId)) {
        setErr("Missing or invalid lessonId query parameter.");
        setLoading(false);
        return;
      }
      if (!communityMode) {
        if (courseId && !UUID_RE.test(courseId)) {
          setErr("Invalid courseId query parameter.");
          setLoading(false);
          return;
        }
        if (cohortId && !UUID_RE.test(cohortId)) {
          setErr("Invalid cohortId query parameter.");
          setLoading(false);
          return;
        }
      }
      setLoading(true);
      setErr(null);
      setLoadedAppearance(null);
      try {
        if (communityMode) {
          const detail = await getCommunityLessonDetail(bearer, lessonId);
          if (cancelled) return;
          setLessonTitle(titleHint || detail.lesson.title || "main.py");
          setLoadedAppearance({
            iconName: detail.lesson.iconName,
            iconBgColor: detail.lesson.iconBgColor,
            iconFgColor: detail.lesson.iconFgColor,
            workspaceMode: detail.lesson.workspaceMode,
          });
          setWorkspaceMode((detail.lesson.workspaceMode as "simple" | "advanced") ?? "simple");
          setStarterFiles([]);
          const py = detail.preview.mainPy?.trim() ? detail.preview.mainPy : "";
          setInitialMainPy(py || '# Starter file is empty.\nprint("Hello!")\n');
          const sorted = [...detail.tests].sort((a, b) => a.ordinal - b.ordinal || a.id.localeCompare(b.id));
          setPreviewLessonTests(
            sorted.map((t) => ({
              id: t.id,
              name: t.name,
              testCode: t.testCode ?? "",
              isRequired: t.isRequired,
              visibility: t.visibility as LessonRuntimeMarkDoneSpec["visibility"],
              description: t.description ?? null,
            }))
          );
          const mapped: DemoTourStep[] = detail.tourSteps.map((t) => ({
            title: t.title,
            body: t.body.length > 0 ? t.body : ["(No body text for this step.)"],
            referenceCode: t.referenceCode ?? undefined,
          }));
          setTourSteps(
            mapped.length > 0
              ? mapped
              : [{ title: "Instructions", body: ["No guided steps yet for this community lesson."] }]
          );
        } else {
          const [detail, testsPayload] = await Promise.all([
            getTeacherLessonDetail(bearer, courseId || undefined, lessonId),
            getTeacherLessonTests(bearer, courseId || undefined, lessonId),
          ]);
          if (cancelled) return;
          setLessonTitle(titleHint || detail.lesson.title || "main.py");
          setLoadedAppearance({
            iconName: detail.lesson.iconName,
            iconBgColor: detail.lesson.iconBgColor,
            iconFgColor: detail.lesson.iconFgColor,
            workspaceMode: detail.lesson.workspaceMode,
          });
          setWorkspaceMode(detail.lesson.workspaceMode ?? "simple");
          setStarterFiles(starterFilesToProjectFiles(detail.lesson.starterFilesJson));
          const py = detail.preview.mainPy?.trim() ? detail.preview.mainPy : "";
          setInitialMainPy(py || '# Starter file is empty. Add "main.py" under Starter in Courses.\nprint("Hello!")\n');
          const sorted = [...testsPayload.tests].sort((a, b) => a.ordinal - b.ordinal || a.id.localeCompare(b.id));
          setPreviewLessonTests(
            sorted.map((t) => ({
              id: t.id,
              name: t.name,
              testCode: t.testCode ?? "",
              isRequired: t.isRequired,
              visibility: t.visibility,
              description: t.description ?? null,
            }))
          );
          const mapped: DemoTourStep[] = detail.tourSteps.map((t) => ({
            title: t.title,
            body: t.body.length > 0 ? t.body : ["(No body text for this step. Edit in Courses.)"],
            referenceCode: t.referenceCode ?? undefined,
          }));
          setTourSteps(
            mapped.length > 0
              ? mapped
              : [{ title: "Instructions", body: ["No guided steps yet. Add them under Courses."] }]
          );
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : "Could not load lesson");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, communityMode, courseId, cohortId, lessonId, titleHint]);

  const eyebrow = communityMode
    ? "Teacher · Preview community lesson"
    : cohortId
      ? "Teacher · Preview as student (cohort lesson)"
      : courseId
        ? "Teacher · Preview as student (course)"
        : "Teacher · Preview as student (lesson library)";

  const headerExtra = communityMode ? (
    <button
      type="button"
      className="btn secondary ide-btn"
      disabled={adding}
      onClick={() => {
        if (!bearer) return;
        void (async () => {
          setAdding(true);
          try {
            const { lessonId: newId } = await postUseCommunityLesson(bearer, lessonId);
            navigate(`/teacher/lessons/${newId}`);
          } catch (e) {
            setErr(e instanceof Error ? e.message : "Could not add lesson");
          } finally {
            setAdding(false);
          }
        })();
      }}
    >
      {adding ? "Adding…" : "Add to my lessons"}
    </button>
  ) : (
    <Link to={`/teacher/lessons/${lessonId}`} className="btn secondary ide-btn" style={{ textDecoration: "none" }}>
      Edit lesson
    </Link>
  );

  if (!bearer) {
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Sign in as a teacher to preview lessons.</p>
          <Link to={buildLoginPath(`${location.pathname}${location.search}`)}>Log in</Link>
        </div>
      </main>
    );
  }

  if (err) {
    return (
      <main className="page">
        <div className="card card-wide">
          <div className="warn-banner" role="alert">
            {err}
          </div>
          <Link to={back.to} className="btn secondary">
            {back.label}
          </Link>
        </div>
      </main>
    );
  }

  if (loading) {
    if (immersiveTheme) {
      return <ImmersiveSplash theme={immersiveTheme} label="Loading lesson preview…" />;
    }
    return (
      <main className="page">
        <div className="card">
          <p className="muted">Loading lesson preview…</p>
        </div>
      </main>
    );
  }

  if (workspaceMode === "advanced") {
    return (
      <StudentLessonAdvancedBody
        key={storageKey}
        eyebrow={eyebrow}
        title={lessonTitle}
        backTo={back.to}
        backLabel={back.label}
        initialFiles={starterFiles}
        filesStorageKey={`${storageKey}-files`}
        lessonTestsForMarkDone={previewLessonTests}
        tourSteps={tourSteps}
        readOnly={false}
        disableLocalPersist
        immersiveTheme={immersiveTheme}
        introMode={introMode}
      />
    );
  }

  return (
    <StudentIdeBody
      key={storageKey}
      eyebrow={eyebrow}
      title={lessonTitle}
      backTo={back.to}
      backLabel={back.label}
      initialMainPy={initialMainPy}
      storageKey={storageKey}
      tourSteps={tourSteps}
      lessonTestsForMarkDone={previewLessonTests}
      disableLocalPersist
      immersiveTheme={immersiveTheme}
      introMode={introMode}
      headerExtra={headerExtra}
    />
  );
}
